import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { AdvanceSearchService } from '../../services/Advance-search/advance-search.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { PincodeLookupController } from '../../common-files/pincode-lookup';

@Component({
  selector: 'app-establishment-branch-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './establishment-branch-config.component.html',
  styleUrl: './establishment-branch-config.component.scss',
})
export class EstablishmentBranchConfigComponent implements OnInit, OnDestroy {
  form: FormGroup;
  code: string = '';
  canManage: boolean = false;
  isLoading: boolean = false;
  readonly pincodeLookup: PincodeLookupController;

  constructor(
    private fb: FormBuilder,
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private advanceSearchService: AdvanceSearchService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      address: [''],
      landmark: [''],
      state: [''],
      district: [''],
      pincode: ['', Validators.pattern(/^\d{6}$/)],
      locationUrl: [''],
      status: [1],
    });
    this.pincodeLookup = new PincodeLookupController(
      this.form,
      this.advanceSearchService
    );
  }

  ngOnInit(): void {
    this.canManage = this.permissionService.can('branch-config', 'can_manage');
    if (this.canManage) {
      this.pincodeLookup.start();
    } else {
      this.form.disable();
    }
    this.load();
  }

  ngOnDestroy(): void {
    this.pincodeLookup.destroy();
  }

  get pincodeControl() {
    return this.pincodeLookup.pincodeControl;
  }

  get isPincodeLoading(): boolean {
    return this.pincodeLookup.isLoading;
  }

  get pincodeLookupStatus() {
    return this.pincodeLookup.status;
  }

  get pincodeLookupMessage(): string {
    return this.pincodeLookup.message;
  }

  get stateList(): string[] {
    return this.pincodeLookup.stateList;
  }

  get districtList(): string[] {
    return this.pincodeLookup.districtList;
  }

  load(): void {
    this.isLoading = true;
    this.establishmentService.getMandir().subscribe({
      next: (res: any) => {
        const mandir = res?.data || {};
        this.code = mandir.code || '';
        this.form.patchValue({
          name: mandir.name || '',
          address: mandir.address || '',
          landmark: mandir.landmark || '',
          state: mandir.state || '',
          district: mandir.district || '',
          pincode: mandir.pincode || '',
          locationUrl: mandir.locationUrl || '',
          status: mandir.status ?? 1,
        }, { emitEvent: false });
        if (this.canManage) {
          this.pincodeLookup.syncFromForm();
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load branch settings.');
      },
    });
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  save(): void {
    const pincode = String(this.form.get('pincode')?.value ?? '');
    if (!this.pincodeLookup.isVerified(pincode)) {
      this.form.get('pincode')?.markAsTouched();
      this.toastService.error(
        this.pincodeLookupMessage || 'Enter a valid, verified 6-digit pincode.'
      );
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.error('Please complete the required branch details.');
      return;
    }
    this.isLoading = true;
    this.establishmentService.updateMandir(this.form.getRawValue()).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Branch settings updated.');
        this.isLoading = false;
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update branch settings.');
      },
    });
  }
}
